// Deep Intel — analytics from the scorebook-PDF play-by-play (atBats docs).
// Fetches lazily on first expand: a season can be 30+ games × ~50 docs, so we
// never block the dashboard's first paint on this.

import React, { useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { FileUp, ChevronDown } from 'lucide-react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Card, shortName } from './ui';
import {
  computeFatigueCurves, computeTTOSplit, computeRallyMath,
  PitcherFatigue,
} from '../../lib/atbat-analytics';
import type { AtBatDoc } from '../../types/dugout';
import type { SeasonData } from './useSeasonStats';

const FatigueCard = ({ curve, playersById }: { curve: PitcherFatigue; playersById: Record<string, any> }) => {
  const name = playersById[curve.pitcherId]?.name || curve.pitcherName || 'Pitcher';
  const usable = curve.buckets.filter(b => b.sPct != null);
  const maxPct = Math.max(...usable.map(b => b.sPct!), 0.01);
  return (
    <div className="px-4 pt-3.5 pb-3">
      <div className="flex justify-between text-[10px] font-extrabold" style={{ color: GS.ink }}>
        <span>{shortName(name)} · strike % by pitch count</span>
        <span style={{ color: GS.ink3 }}>{curve.games} games</span>
      </div>
      <div className="flex gap-2.5 items-end mt-2.5" style={{ height: 84 }}
        role="img"
        aria-label={`Strike percentage by pitch count for ${name}: ${curve.buckets.map(b => `${b.label}: ${b.sPct != null ? Math.round(b.sPct * 100) + ' percent' : 'insufficient data'}`).join(', ')}`}
      >
        {curve.buckets.map((b, i) => {
          const isLast = i === curve.buckets.length - 1;
          const faded = curve.fades && isLast;
          return (
            <div key={b.label} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
              <span className="text-[11px] font-black" style={{ color: faded ? GS.red : GS.ink }}>
                {fmtStat(b.sPct, 'pct')}
              </span>
              <div
                className="w-full rounded-t-lg rounded-b"
                style={{
                  height: b.sPct != null ? `${Math.max(8, (b.sPct / maxPct) * 70)}%` : 4,
                  background: b.sPct == null ? GS.panel2 : faded ? '#E5484D' : GS.green2,
                }}
              />
              <span className="text-[8px] font-extrabold uppercase" style={{ color: GS.ink3 }}>P {b.label}</span>
            </div>
          );
        })}
      </div>
      {curve.fades && (
        <p className="text-[10px] font-semibold leading-relaxed mt-2" style={{ color: GS.ink2 }}>
          Command fades late: <b style={{ color: GS.ink }}>worth planning the hook a bucket earlier</b>.
        </p>
      )}
    </div>
  );
};

export const DeepIntelSection = ({ team, data, playersById, playersPitch }: {
  team: any;
  data: SeasonData;
  playersById: Record<string, any>;
  playersPitch: boolean;
}) => {
  const [state, setState] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [atBats, setAtBats] = useState<AtBatDoc[]>([]);

  const load = async () => {
    if (state !== 'idle') return;
    setState('loading');
    try {
      const completed = data.sortedGames;
      const results = await Promise.all(completed.map(async g => {
        try {
          const snap = await getDocs(collection(db, `teams/${team.id}/games/${g.id}/atBats`));
          return snap.docs.map(d => ({ id: d.id, ...d.data() })) as AtBatDoc[];
        } catch {
          return [] as AtBatDoc[];
        }
      }));
      setAtBats(results.flat());
    } finally {
      setState('loaded');
    }
  };

  if (state !== 'loaded') {
    return (
      <Card className="p-4">
        <button onClick={load} disabled={state === 'loading'} className="w-full flex items-center gap-3 text-left">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: GS.greenSoft, border: `1px solid ${GS.green}` }}>
            <ChevronDown className="w-4 h-4" style={{ color: GS.greenInk }} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-black" style={{ color: GS.ink }}>
              {state === 'loading' ? 'Reading your scorebooks…' : 'Open Deep Intel'}
            </p>
            <p className="text-[10px] font-semibold mt-0.5" style={{ color: GS.ink2 }}>
              Fatigue curves, lineup-turn splits, and rally math from your pitch-by-pitch imports.
            </p>
          </div>
        </button>
      </Card>
    );
  }

  if (atBats.length === 0) {
    return (
      <Card className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: GS.panel2 }}>
          <FileUp className="w-4 h-4" style={{ color: GS.ink3 }} />
        </div>
        <p className="text-[11px] font-semibold leading-relaxed" style={{ color: GS.ink2 }}>
          <b style={{ color: GS.ink }}>No scorebook imports yet.</b>{' '}
          Import a scorebook PDF from GameChanger (Games → Import) to unlock fatigue curves, lineup-turn splits, and rally math: data no box score shows.
        </p>
      </Card>
    );
  }

  const fatigue = playersPitch ? computeFatigueCurves(atBats) : [];
  const tto = playersPitch ? computeTTOSplit(atBats) : null;
  const rally = computeRallyMath(atBats);
  const nothing = fatigue.length === 0 && !tto && !rally;

  return (
    <Card>
      {fatigue.slice(0, 3).map((c, i) => (
        <div key={c.pitcherId} style={{ borderTop: i > 0 ? `1px solid ${GS.line}` : undefined }}>
          <FatigueCard curve={c} playersById={playersById} />
        </div>
      ))}

      {tto && (
        <div className="flex" style={{ borderTop: `1px solid ${GS.line}` }}>
          <div className="flex-1 px-4 py-3 text-center">
            <div className="text-lg font-black" style={{ color: GS.ink }}>{fmtStat(tto.firstTime.avg, 'avg3')}</div>
            <div className="text-[8.5px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>Opp. AVG · 1st time through</div>
          </div>
          <div className="flex-1 px-4 py-3 text-center" style={{ borderLeft: `1px solid ${GS.line}` }}>
            <div className="text-lg font-black" style={{ color: tto.secondPlus.avg != null && tto.firstTime.avg != null && tto.secondPlus.avg > tto.firstTime.avg ? GS.red : GS.ink }}>
              {fmtStat(tto.secondPlus.avg, 'avg3')}
            </div>
            <div className="text-[8.5px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>2nd time through</div>
          </div>
        </div>
      )}

      {rally && (
        <div className="flex" style={{ borderTop: `1px solid ${GS.line}`, background: GS.greenSoft }}>
          <div className="flex-1 px-4 py-3 text-center">
            <div className="text-lg font-black" style={{ color: GS.greenInk }}>{rally.leadoffOn.runsPerInning.toFixed(1)}</div>
            <div className="text-[8.5px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>Runs/inning · leadoff man on</div>
          </div>
          <div className="flex-1 px-4 py-3 text-center" style={{ borderLeft: `1px solid ${GS.line}` }}>
            <div className="text-lg font-black" style={{ color: GS.ink }}>{rally.leadoffOut.runsPerInning.toFixed(1)}</div>
            <div className="text-[8.5px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.06em' }}>Leadoff man out</div>
          </div>
        </div>
      )}

      {nothing && (
        <p className="px-4 py-3.5 text-[11px] font-semibold leading-relaxed" style={{ color: GS.ink2 }}>
          Scorebook data found, but not enough of it yet for reliable reads. Fatigue and rally splits appear after a few more imported games.
        </p>
      )}

      {!nothing && (
        <p className="px-4 py-2.5 text-[10px] font-semibold leading-relaxed" style={{ color: GS.ink3, borderTop: `1px solid ${GS.line}` }}>
          Built from the <b style={{ color: GS.ink }}>pitch-by-pitch scorebook PDFs</b> you import. Youth-size samples: read these as signals, not verdicts.
        </p>
      )}
    </Card>
  );
};
